import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  FileText,
  Calendar,
  User,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

// TDMS Account Type Labels
const TDMS_TYPE_LABELS: Record<string, string> = {
  CURRENT_ASSET: 'Donen Varliklar',
  FIXED_ASSET: 'Duran Varliklar',
  SHORT_TERM_LIABILITY: 'Kisa Vadeli Yab. Kaynaklar',
  LONG_TERM_LIABILITY: 'Uzun Vadeli Yab. Kaynaklar',
  EQUITY: 'Oz Kaynaklar',
  INCOME: 'Gelirler',
  COST: 'Giderler/Maliyetler',
  FREE: 'Serbest',
  OFF_BALANCE: 'Nazim Hesaplar',
};

interface JournalEntryLine {
  id: string;
  accountId: string;
  description?: string | null;
  debit: number;
  credit: number;
  account: {
    id: string;
    code: string;
    name: string;
    type: string;
  };
  tradingCustomerId?: string | null;
  supplierId?: string | null;
  tradingCustomer?: {
    id: string;
    companyName: string;
  } | null;
  supplier?: {
    id: string;
    companyName: string;
  } | null;
}

export function TradingJournalEntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('tradingAccounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const { user } = useAuthStore();

  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const { data: entry, isLoading } = trpc.tradingJournalEntry.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const postMutation = trpc.tradingJournalEntry.post.useMutation({
    onSuccess: () => {
      utils.tradingJournalEntry.byId.invalidate(id);
      utils.tradingJournalEntry.list.invalidate();
      utils.tradingJournalEntry.stats.invalidate();
      utils.tradingAccount.list.invalidate();
      utils.tradingAccount.getTree.invalidate();
      setIsPostDialogOpen(false);
    },
  });

  const voidMutation = trpc.tradingJournalEntry.void.useMutation({
    onSuccess: () => {
      utils.tradingJournalEntry.byId.invalidate(id);
      utils.tradingJournalEntry.list.invalidate();
      utils.tradingJournalEntry.stats.invalidate();
      utils.tradingAccount.list.invalidate();
      utils.tradingAccount.getTree.invalidate();
      setIsVoidDialogOpen(false);
    },
  });

  const deleteMutation = trpc.tradingJournalEntry.delete.useMutation({
    onSuccess: () => {
      navigate('/trading/accounting');
    },
  });

  const handlePost = () => {
    if (user) {
      postMutation.mutate(id!);
    }
  };

  const handleVoid = () => {
    if (voidReason.trim()) {
      voidMutation.mutate({ id: id!, reason: voidReason });
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate(id!);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-lg font-medium text-muted-foreground">{t('journalDetail.notFound', 'Journal entry not found')}</p>
        <Button variant="link" onClick={() => navigate('/trading/accounting')}>
          {t('journalDetail.backToAccounting', 'Back to Accounting')}
        </Button>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-700',
    POSTED: 'bg-green-100 text-green-700',
    VOIDED: 'bg-red-100 text-red-700',
  };

  const typeColors: Record<string, string> = {
    CURRENT_ASSET: 'bg-blue-100 text-blue-700',
    FIXED_ASSET: 'bg-blue-200 text-blue-800',
    SHORT_TERM_LIABILITY: 'bg-red-100 text-red-700',
    LONG_TERM_LIABILITY: 'bg-red-200 text-red-800',
    EQUITY: 'bg-purple-100 text-purple-700',
    INCOME: 'bg-green-100 text-green-700',
    COST: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/trading/accounting')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{entry.entryNumber}</h1>
              <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[entry.status]}`}>
                {entry.status}
              </span>
            </div>
            <p className="text-muted-foreground">{entry.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {entry.status === 'DRAFT' && (
            <>
              <Button variant="outline" className="h-8" onClick={() => navigate(`/trading/accounting/journal-entries/${id}/edit`)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('journalDetail.edit', 'Edit')}
              </Button>
              <Button variant="destructive" className="h-8" onClick={() => setIsDeleteDialogOpen(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                {t('journalDetail.delete', 'Delete')}
              </Button>
              <Button className="h-8" onClick={() => setIsPostDialogOpen(true)}>
                <CheckCircle className="mr-2 h-4 w-4" />
                {t('journalDetail.postEntry', 'Post Entry')}
              </Button>
            </>
          )}
          {entry.status === 'POSTED' && (
            <Button variant="destructive" className="h-8" onClick={() => setIsVoidDialogOpen(true)}>
              <XCircle className="mr-2 h-4 w-4" />
              {t('journalDetail.voidEntry', 'Void Entry')}
            </Button>
          )}
        </div>
      </div>

      {/* Entry Details */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 border border-border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">{t('journalDetail.entryDetails', 'Entry Details')}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('journalDetail.date', 'Date')}</p>
                <p className="font-medium">{new Date(entry.date).toLocaleDateString(dateLocale)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('journalDetail.referenceType', 'Reference Type')}</p>
                <p className="font-medium">{entry.referenceType || 'Manual'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('journalDetail.createdBy', 'Created By')}</p>
                <p className="font-medium">
                  {entry.createdBy ? `${entry.createdBy.firstName} ${entry.createdBy.lastName}` : '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t('journalDetail.createdAt', 'Created At')}</p>
                <p className="font-medium">{new Date(entry.createdAt).toLocaleString(dateLocale)}</p>
              </div>
            </div>
            {entry.postedBy && (
              <>
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('journalDetail.postedBy', 'Posted By')}</p>
                    <p className="font-medium">
                      {`${entry.postedBy.firstName} ${entry.postedBy.lastName}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('journalDetail.postedAt', 'Posted At')}</p>
                    <p className="font-medium">
                      {entry.postedAt ? new Date(entry.postedAt).toLocaleString(dateLocale) : '-'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="border border-border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">{t('journalDetail.entryTotals', 'Entry Totals')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('journalDetail.totalDebits', 'Total Debits')}</span>
              <span className="text-xl font-bold font-mono">
                {formatCurrency(entry.totalDebit, entry.currency)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('journalDetail.totalCredits', 'Total Credits')}</span>
              <span className="text-xl font-bold font-mono">
                {formatCurrency(entry.totalCredit, entry.currency)}
              </span>
            </div>
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('journalDetail.currency', 'Currency')}</span>
                <span className="font-medium">{entry.currency}</span>
              </div>
            </div>
            {entry.status === 'POSTED' && (
              <div className="mt-4 p-3 bg-green-50 text-green-700 text-sm border border-green-200">
                {t('journalDetail.postedMessage', 'This entry has been posted to the general ledger.')}
              </div>
            )}
            {entry.status === 'VOIDED' && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 text-sm border border-red-200">
                {t('journalDetail.voidedMessage', 'This entry has been voided and reversed.')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Journal Lines */}
      <div className="border border-border bg-card">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">{t('journalDetail.journalLines', 'Journal Lines')} ({entry.lines.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">#</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('journalDetail.account', 'Account')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('journalDetail.description', 'Description')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium">{t('journalDetail.debit', 'Debit')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium">{t('journalDetail.credit', 'Credit')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entry.lines.map((line: JournalEntryLine, index: number) => (
                <tr key={line.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm text-muted-foreground">{index + 1}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/trading/accounting/accounts/${line.account.id}`}
                      className="hover:underline"
                    >
                      <span className="font-mono text-sm">{line.account.code}</span>
                      <span className="ml-2 text-sm">{line.account.name}</span>
                    </Link>
                    <span className={`ml-2 text-xs px-2 py-0.5 ${typeColors[line.account.type] || 'bg-gray-100 text-gray-700'}`}>
                      {TDMS_TYPE_LABELS[line.account.type] || line.account.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {line.description || '-'}
                    {(line.tradingCustomer || line.supplier) && (
                      <p className="text-xs text-muted-foreground">
                        {line.tradingCustomer
                          ? `${t('journalDetail.customer', 'Customer')}: ${line.tradingCustomer.companyName}`
                          : `${t('journalDetail.supplier', 'Supplier')}: ${line.supplier?.companyName}`}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {line.debit > 0 ? formatCurrency(line.debit, entry.currency) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {line.credit > 0 ? formatCurrency(line.credit, entry.currency) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-medium">
              <tr>
                <td className="px-4 py-3" colSpan={3}>
                  <span className="text-sm">{t('journalDetail.totals', 'Totals')}</span>
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(entry.totalDebit, entry.currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(entry.totalCredit, entry.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Post Confirmation Dialog */}
      <Dialog open={isPostDialogOpen} onOpenChange={setIsPostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('journalDetail.postTitle', 'Post Journal Entry')}</DialogTitle>
            <DialogDescription>
              {t('journalDetail.postConfirmation', 'Are you sure you want to post this journal entry? This will update account balances and the entry cannot be modified afterwards.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-muted p-4 space-y-2 border border-border">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('journalDetail.entryNumber', 'Entry Number')}</span>
                <span className="font-mono">{entry.entryNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('journalDetail.date', 'Date')}</span>
                <span>{new Date(entry.date).toLocaleDateString(dateLocale)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('journalDetail.totalAmount', 'Total Amount')}</span>
                <span className="font-mono">{formatCurrency(entry.totalDebit, entry.currency)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsPostDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button className="h-8" onClick={handlePost} disabled={postMutation.isPending}>
              {postMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('journalDetail.postEntry', 'Post Entry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <Dialog open={isVoidDialogOpen} onOpenChange={setIsVoidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('journalDetail.voidTitle', 'Void Journal Entry')}</DialogTitle>
            <DialogDescription>
              {t('journalDetail.voidConfirmation', 'Are you sure you want to void this journal entry? This will reverse all account balance changes made by this entry.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
              {t('journalDetail.voidWarning', 'This action cannot be undone. The entry will be marked as voided and all account balances will be reversed.')}
            </div>
            <div className="space-y-2">
              <Label htmlFor="voidReason">{t('journalDetail.voidReason', 'Reason for voiding')} *</Label>
              <Input
                id="voidReason"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder={t('journalDetail.voidReasonPlaceholder', 'Enter reason for voiding this entry...')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsVoidDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={handleVoid}
              disabled={voidMutation.isPending || !voidReason.trim()}
            >
              {voidMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('journalDetail.voidEntry', 'Void Entry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('journalDetail.deleteTitle', 'Delete Journal Entry')}</DialogTitle>
            <DialogDescription>
              {t('journalDetail.deleteConfirmation', 'Are you sure you want to delete this draft journal entry? This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('journalDetail.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
